import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Quote, ArrowUpRight, Share2, Sparkles, BookOpen } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import useDailyTheme from "@/hooks/useDailyTheme";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

const WHATSAPP = "5511999999999"; // placeholder, will be normalized if env-available

function shareOnWhatsApp(title, phrase, verse) {
  const text = `✨ ${phrase}\n\n📖 ${verse}\n(“${title}”, Carlos Medeiros)`;
  const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener");
}

export default function SermonOfTheDay() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const r = useReveal();
  const { theme } = useDailyTheme();

  useEffect(() => {
    api
      .get("/sermon-of-today")
      .then(({ data }) => setS(data))
      .catch(() => setS(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !s) return null;

  // Fallback theological, quando o sermão do dia não tem derivatives,
  // usa os campos sincronizados do /api/daily (mesmo tema/versículo do dia).
  const impact = s.derivatives?.impact_phrase || theme?.phrase || null;
  const daily =
    s.derivatives?.daily_message ||
    theme?.devotional?.lead_paragraph ||
    s.preview ||
    null;
  const application =
    s.derivatives?.application_phrase ||
    (theme?.prayer
      ? // Pega 1ª frase via regex de pontuação tolerante a "Sr.", "Dr." etc.
        (theme.prayer.match(/^[\s\S]+?[.!?](?=\s+[A-ZÁ-Ú])/) || [theme.prayer])[0].trim()
      : null);

  return (
    <section
      id="sermao-de-hoje"
      data-testid="sermon-of-the-day-section"
      className="section-pad bg-[#0B1E36] text-white relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-[#C5A059]/30" />
      <div className="max-w-6xl mx-auto px-6 md:px-8 relative">
        <div ref={r} className="reveal">
          <div className="flex items-center gap-2 mb-6 text-[#C5A059]">
            <Sparkles size={14} strokeWidth={1.5} />
            <span className="text-[0.72rem] uppercase tracking-[0.3em] font-medium">
              Sermão de hoje
            </span>
            <span className="text-[0.72rem] tracking-[0.18em] text-white/50 ml-2">
              ·{" "}
              {new Date().toLocaleDateString("pt-BR", {
                weekday: "long",
                day: "2-digit",
                month: "long",
              })}
            </span>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="text-[0.7rem] uppercase tracking-[0.28em] text-[#C5A059] mb-5">
                {s.category}
                {s.verse_reference ? ` · ${s.verse_reference}` : ""}
              </p>
              <h2
                data-testid="sotd-title"
                className="font-serif-display text-[2.5rem] sm:text-5xl md:text-[3.75rem] lg:text-[4.25rem] leading-[1.03] tracking-[-0.025em] font-medium"
              >
                {s.title}
              </h2>

              {impact && (
                <div className="mt-10 flex items-start gap-4">
                  <Quote
                    size={28}
                    strokeWidth={1.25}
                    className="text-[#C5A059] mt-1 shrink-0"
                  />
                  <p
                    data-testid="sotd-impact-phrase"
                    className="font-serif-display italic text-[1.5rem] md:text-[1.75rem] text-white/92 leading-[1.4] tracking-[-0.01em]"
                  >
                    {impact}
                  </p>
                </div>
              )}

              {daily && (
                <p
                  data-testid="sotd-daily-message"
                  className="mt-10 text-[1.05rem] md:text-[1.15rem] leading-[1.85] font-light text-white/75 max-w-2xl"
                >
                  {daily}
                </p>
              )}

              <div className="mt-12 flex flex-wrap items-center gap-4">
                <Link
                  to={`/pregacoes/${s.slug || s.id}`}
                  data-testid="sotd-read-full"
                  onClick={() => track("click_sermon", { slug: s.slug, category: s.category, position: "sermon_of_day" })}
                  className="inline-flex items-center gap-2 bg-[#C5A059] hover:bg-[#B58F4A] text-[#0B1E36] px-6 py-3 text-xs uppercase tracking-[0.25em] font-medium transition-colors"
                >
                  <BookOpen size={14} strokeWidth={1.5} />
                  Ler sermão completo
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
                {impact && (
                  <button
                    onClick={() =>
                      shareOnWhatsApp(s.title, impact, s.verse_reference || "")
                    }
                    data-testid="sotd-share-whatsapp"
                    className="inline-flex items-center gap-2 border border-white/25 hover:border-[#C5A059] hover:text-[#C5A059] px-6 py-3 text-xs uppercase tracking-[0.25em] transition-colors"
                  >
                    <Share2 size={14} strokeWidth={1.5} />
                    Compartilhar
                  </button>
                )}
              </div>
            </div>

            {application && (
              <div className="lg:col-span-5 lg:pl-8 lg:border-l border-white/10">
                <p className="text-[0.7rem] uppercase tracking-[0.28em] text-[#C5A059]">
                  Aplicação prática
                </p>
                <p
                  data-testid="sotd-application-phrase"
                  className="mt-5 font-serif-display text-[1.65rem] md:text-[1.85rem] italic text-white/95 leading-[1.45] tracking-[-0.012em]"
                >
                  “{application}”
                </p>
                <p className="mt-10 text-[0.78rem] uppercase tracking-[0.22em] text-white/45">
                  Uma palavra por dia · agenda semanal
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
